import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "supabase/database/api.ts";

const CampaignContext = createContext({
  campaign: null,
  loading: true,
  refreshCampaign: () => {}
});

const CAMPAIGN_CACHE_KEY = 'lightrainair_campaign_cache';
const CACHE_VERSION = 'v1';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const CampaignProvider = ({ children }) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load from localStorage cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(CAMPAIGN_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp, version } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (version === CACHE_VERSION && age < CACHE_DURATION && data) {
        
        return data;
      }

      localStorage.removeItem(CAMPAIGN_CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading campaign cache:', error);
      localStorage.removeItem(CAMPAIGN_CACHE_KEY);
      return null;
    }
  }, []);

  // Save to localStorage cache
  const saveToCache = useCallback((data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      localStorage.setItem(CAMPAIGN_CACHE_KEY, JSON.stringify(cacheData));
      
    } catch (error) {
      console.error('Error saving campaign to cache:', error);
    }
  }, []);

  const loadCampaign = useCallback(async (bypassCache = false) => {
    try {
      // Try loading from cache first (instant)
      if (!bypassCache) {
        const cachedCampaign = loadFromCache();
        if (cachedCampaign) {
          setCampaign(cachedCampaign);
          setLoading(false);

          // Fetch fresh data in background
          api.heavenlyCultureCampaignQueries.getActiveCampaigns({}).then((freshCampaign) => {
            if (JSON.stringify(freshCampaign) !== JSON.stringify(cachedCampaign)) {
              setCampaign(freshCampaign);
              saveToCache(freshCampaign);
            }
          }).catch(err => console.error('Background campaign refresh failed:', err));

          return;
        }
      }

      // No cache - fetch from server
      const activeCampaign = await api.heavenlyCultureCampaignQueries.getActiveCampaigns({});

      setCampaign(activeCampaign);
      saveToCache(activeCampaign);
      setLoading(false);
    } catch (error) {
      console.error("Error loading campaign:", error);
      setLoading(false);
    }
  }, [loadFromCache, saveToCache]);

  // Initial load on mount
  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  // Refresh function
  const refreshCampaign = useCallback(() => {
    loadCampaign(true);
  }, [loadCampaign]);

  const value = {
    campaign,
    loading,
    refreshCampaign
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export const useCampaign = () => {
  return useContext(CampaignContext);
};
